import http  from 'axios';

export const AttributeChangeHeaderStatus: Record<number, string> = {
    100 : "Requested",
    200 : "Viewed",
    300 : "Accepted",
    400 : "Executing",
    500 : "Completed",
    600 : "Expired",
    700 : "Failed",
    800 : "Rejected",
} as const;

export interface AttributeChangeHeader {
    id: string;
    systemId: string;
    userId: string;
    status: number;
    notificationType: string;
    notificationValue: string;
    notificationSentTime: string;
    transactionId: string;
    createdDatetime: string;
    createdBy: string;
    updatedDatetime: string;
    updatedBy: string;
}

export interface AttributeChangeLine {
    id: string;
    attributeChangeHeaderId: string;
    attributeName: string;
    oldValue: string;
    newValue: string;
    status: number;
    transactionId: string;
    createdDatetime: string;
    createdBy: string;
}

export interface B2CTransaction {
    attributeChangeHeader: AttributeChangeHeader;
    attributeChangeDetails: AttributeChangeLine[];
}

export interface TransactionsResult {
    data: B2CTransaction[];
    count: number;
    page: number;
}

export interface TransactionViewed {
    id: string;
    status: number;
}

// Mock data for development
const mockTransactions: B2CTransaction[] = [
  {
    attributeChangeHeader: {
      id: "1",
      systemId: "SYS001",
      userId: "user123",
      status: 100,
      notificationType: "ProfileUpdate",
      notificationValue: "Email Change Request",
      notificationSentTime: new Date().toISOString(),
      transactionId: "txn001",
      createdDatetime: new Date().toISOString(),
      createdBy: "system",
      updatedDatetime: new Date().toISOString(),
      updatedBy: "system"
    },
    attributeChangeDetails: [
      {
        id: "detail1",
        attributeChangeHeaderId: "1",
        attributeName: "Email",
        oldValue: "old.email@example.com",
        newValue: "new.email@example.com",
        status: 100,
        transactionId: "txn001",
        createdDatetime: new Date().toISOString(),
        createdBy: "user"
      },
      {
        id: "detail2",
        attributeChangeHeaderId: "1",
        attributeName: "PhoneNumber",
        oldValue: "+1234567890",
        newValue: "+0987654321",
        status: 100,
        transactionId: "txn001",
        createdDatetime: new Date().toISOString(),
        createdBy: "user"
      }
    ]
  },
  {
    attributeChangeHeader: {
      id: "2",
      systemId: "SYS002",
      userId: "user456",
      status: 200,
      notificationType: "AddressUpdate",
      notificationValue: "Address Change Request",
      notificationSentTime: new Date(Date.now() - 86400000).toISOString(),
      transactionId: "txn002",
      createdDatetime: new Date(Date.now() - 86400000).toISOString(),
      createdBy: "system",
      updatedDatetime: new Date().toISOString(),
      updatedBy: "system"
    },
    attributeChangeDetails: [
      {
        id: "detail3",
        attributeChangeHeaderId: "2",
        attributeName: "Address",
        oldValue: "123 Old Street, Old City",
        newValue: "456 New Avenue, New City",
        status: 200,
        transactionId: "txn002",
        createdDatetime: new Date(Date.now() - 86400000).toISOString(),
        createdBy: "user"
      }
    ]
  }
];

const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true'; // Defaults to false if not set

const getTransactions = async (page = 0, size = 10) : Promise<TransactionsResult> => {
    if (USE_MOCK_DATA) {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 500));
        return {
            data: mockTransactions,
            count: mockTransactions.length,
            page: page
        };
    }
    
    let url = `${process.env.REACT_APP_API_URL}/transactions?page=${page}&size=${size}`;
    const result = (await http.get<TransactionsResult>(url)).data;
    return result;
}

const getTransaction = async (transcationId: string) => {
    if (USE_MOCK_DATA) {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 300));
        const transaction = mockTransactions.find(t => t.attributeChangeHeader.transactionId === transcationId) || mockTransactions[0];
        return { data: transaction };
    }
    
    let url = `${process.env.REACT_APP_API_URL}/transactions/${transcationId}`;
    return await http.get<B2CTransaction>(url);
}

const updateTransactionStatus = async (transactionId: string, accept: boolean) => {
    if (USE_MOCK_DATA) {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 400));
        console.log(`Mock: Transaction ${transactionId} ${accept ? 'accepted' : 'rejected'}`);
        return 200;
    }
    
    let url = `${process.env.REACT_APP_API_URL}/transactions/${transactionId}?Accept=${accept? 'yes': 'no'}`;

    try {
        const res = await http.post(url);
        return res.status;
    } catch (err) {
        console.log(err);
        return null;
    }
}

const setViewed = async (transactionId: string) => {
    if (USE_MOCK_DATA) {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 200));
        console.log(`Mock: Transaction ${transactionId} marked as viewed`);
        return 200;
    }
    
    let url = `${process.env.REACT_APP_API_URL}/transactions/${transactionId}}`;

    try {
        let data = { id: transactionId, status: 200 };
        const res = await http.put(url, data);
        return res.status;
    } catch (err) {
        console.log(err);
        return null;
    }
}

const TransactionService = {
    getTransaction, 
    updateTransactionStatus,
    getTransactions,
    setViewed
}

export default TransactionService;